/* eslint-disable @typescript-eslint/no-unused-vars */
import { Checkbox, Tag } from 'antd';
import getFormatMessage from '../../../locales/get-format-message';
import { CommonSelect } from '../../../components/select';
import styles from './index.module.scss';
import { RightOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { CustomPopover } from '../../../components/custom-popover';
import classNames from 'classnames';

interface IProps {
  sdkInfo: IEngineVersion[];
  onVersionChange: (values: IEngineVersion[]) => void;
}

interface IEngineVersion {
  sdkName: string;
  sdkVersionList: string[];
}

export const EngineVersionSelect = (props: IProps) => {
  const { sdkInfo, onVersionChange } = props;
  const [selectedSdk, setSelectedSdk] = useState<IEngineVersion>(sdkInfo[0]);
  const [selectedVersions, setSelectedVersions] = useState<IEngineVersion[]>(
    []
  );

  useEffect(() => {
    setSelectedSdk(sdkInfo[0]);
    onClear();
  }, [sdkInfo]);

  const onClear = () => {
    const data: IEngineVersion[] = [];
    sdkInfo.forEach((item) => {
      data.push({
        sdkName: item.sdkName,
        sdkVersionList: [],
      });
    });
    setSelectedVersions(data);
    onVersionChange(data);
  };

  const onValuesChange = (newValues: IEngineVersion[]) => {
    onVersionChange(newValues);
    setSelectedVersions(newValues);
  };

  const dropdownRender = () => {
    const onChange = (
      ev: CheckboxChangeEvent,
      version: { sdkName: string; sdkVersion: string }
    ) => {
      const { checked } = ev.target;
      const newData: IEngineVersion[] = [];
      if (checked) {
        selectedVersions.forEach((item) => {
          if (item.sdkName === version.sdkName) {
            const sdkVersionList = Object.assign([], item.sdkVersionList);
            if (!sdkVersionList.includes(version.sdkVersion)) {
              sdkVersionList.push(version.sdkVersion);
            }
            newData.push({
              sdkName: version.sdkName,
              sdkVersionList,
            });
          } else {
            newData.push(item);
          }
        });
      } else {
        selectedVersions.forEach((item) => {
          if (item.sdkName === version.sdkName) {
            const sdkVersionList = Object.assign([], item.sdkVersionList);
            if (sdkVersionList.includes(version.sdkVersion)) {
              sdkVersionList.splice(
                sdkVersionList.indexOf(version.sdkVersion),
                1
              );
            }
            newData.push({
              sdkName: version.sdkName,
              sdkVersionList,
            });
          } else {
            newData.push(item);
          }
        });
      }
      onValuesChange(newData);
    };

    const checkAll = (operateSdk: IEngineVersion, checked: boolean) => {
      const newData: IEngineVersion[] = [];
      if (checked) {
        selectedVersions.forEach((item) => {
          if (item.sdkName === operateSdk.sdkName) {
            newData.push(operateSdk);
          } else {
            newData.push(item);
          }
        });
      } else {
        selectedVersions.forEach((item) => {
          if (item.sdkName === operateSdk.sdkName) {
            newData.push({
              sdkName: item.sdkName,
              sdkVersionList: [],
            });
          } else {
            newData.push(item);
          }
        });
      }
      onValuesChange(newData);
    };

    const isSelectSdkAll = (operateSdk: IEngineVersion) => {
      const selectItem = selectedVersions.find(
        (item) => item.sdkName === operateSdk.sdkName
      );
      return (
        selectItem &&
        operateSdk.sdkVersionList.length ===
          selectItem?.sdkVersionList.length &&
        selectItem?.sdkVersionList.length !== 0
      );
    };

    const isSdkIndeterminate = (operateSdk: IEngineVersion) => {
      const selectItem = selectedVersions.find(
        (item) => item.sdkName === operateSdk.sdkName
      );
      return (
        selectItem &&
        operateSdk.sdkVersionList.length > selectItem?.sdkVersionList.length &&
        selectItem?.sdkVersionList.length !== 0
      );
    };

    return (
      <div className={styles.sdkSelectMenu}>
        <div className={styles.sdkSelectLeft}>
          {[...sdkInfo].map((item) => {
            return (
              <div
                className={classNames(styles.sdkLabel, {
                  [styles.selected]: selectedSdk === item,
                })}
                onClick={() => setSelectedSdk(item)}
              >
                <Checkbox
                  onChange={(ev) => checkAll(item, ev.target.checked)}
                  checked={isSelectSdkAll(item)}
                  indeterminate={isSdkIndeterminate(item)}
                />
                <span className="ellipsis">{item.sdkName}</span>
                <RightOutlined />
              </div>
            );
          })}
        </div>

        <div className={styles.sdkSelectRight}>
          {selectedSdk &&
            [...selectedSdk.sdkVersionList].map((sdkVersion) => {
              return (
                <Checkbox
                  checked={selectedVersions.some(
                    (item) =>
                      item.sdkName === selectedSdk.sdkName &&
                      item.sdkVersionList.includes(sdkVersion)
                  )}
                  onChange={(ev) =>
                    onChange(ev, {
                      sdkName: selectedSdk.sdkName,
                      sdkVersion,
                    })
                  }
                >
                  {sdkVersion}
                </Checkbox>
              );
            })}
        </div>
      </div>
    );
  };

  const values = selectedVersions.flatMap((item) => {
    if (item.sdkVersionList?.length > 0) {
      return item.sdkVersionList.map((version) => `${item.sdkName}/${version}`);
    }
    return [];
  });

  const maxTagRender = () => {
    const getAllValues = () => {
      return (
        <div>
          {values.map((value) => {
            return <div>{value}</div>;
          })}
        </div>
      );
    };
    let selectNum = 0;
    selectedVersions.forEach((item) => {
      selectNum += item.sdkVersionList.length;
    });
    return (
      <CustomPopover
        content={getAllValues()}
        overlayClassName={styles.allSdkValues}
      >
        <span>{`+${selectNum - 3}`}</span>
      </CustomPopover>
    );
  };

  return (
    <CommonSelect
      value={values}
      className={styles.sdkSelector}
      dropdownRender={dropdownRender}
      getPopupContainer={(el) => el.parentNode}
      showSearch={false}
      mode="multiple"
      popupClassName={styles.sdkSelectPopup}
      maxTagCount={3}
      allowClear
      dropdownMatchSelectWidth={false}
      maxTagPlaceholder={maxTagRender()}
      onChange={(values: string[]) => {
        const data: IEngineVersion[] = [];
        sdkInfo.forEach((item) => {
          data.push({
            sdkName: item.sdkName,
            sdkVersionList: [],
          });
        });
        values.forEach((item) => {
          const value = item.split('/');
          const sdkName = value[0];
          const sdkVersion = value[1];

          const targetItem = data.find((d) => d.sdkName === sdkName);

          if (
            targetItem &&
            sdkVersion &&
            !targetItem.sdkVersionList.includes(sdkVersion)
          ) {
            targetItem.sdkVersionList.push(sdkVersion);
          }
        });
        onValuesChange(data);
      }}
      prefix="引擎"
    />
  );
};
